// You can only write app specific functions in this module
import { BetResponseCode, BetStatus, parseNumberByPrecision } from '@pascal/pascal-core';
import { additionalBetAmountChangingButtons, autoCashOutDataKeys } from '../enums';
import { doBetStore, marketsStore } from '../store';
import {
  divide as divideNumbers,
  multiply,
  plus as plusNumbers,
  minus as minusNumbers,
  toFixed,
} from './arithmeticOperations';

export const decidedCurrentValue = (
  condition,
  value,
  finishedValue,
  showNotification,
  responseCode
) => {
  const currentValue = condition ? value : finishedValue;
  if (currentValue === finishedValue && showNotification && responseCode) {
    showNotification({ responseCode });
  }

  return condition ? value : finishedValue;
};

export const changeBetAmount = (
  autoCashOutData,
  isDemo,
  functionName,
  callback,
  limits,
  balance,
  amount,
  showNotification
) => {
  const { plus, min, allIn, double, divide, max, minus } = additionalBetAmountChangingButtons;
  const { InValidMaxBet, InValidMinBet, InsufficientFunds, InValidMaxAutoCashOutOdd } =
    BetResponseCode;
  const { minBet, maxBet, maxWin } = limits;

  const checkAutoCashOutOddWithBetAmount = (changedBetAmount) => {
    if (autoCashOutData) {
      const { isOpened, autoCashOutOdd } = autoCashOutData;
      const currentMaxAutoCashOutOdd = divideNumbers(maxWin, changedBetAmount);
      if (isOpened && autoCashOutOdd > currentMaxAutoCashOutOdd) {
        doBetStore.updateAutoCashOutData(
          autoCashOutDataKeys.autoCashOutMaxOdd,
          toFixed(currentMaxAutoCashOutOdd, 2)
        );
        doBetStore.updateAutoCashOutData(
          autoCashOutDataKeys.autoCashOutOdd,
          toFixed(currentMaxAutoCashOutOdd, 2)
        );
        showNotification({ responseCode: InValidMaxAutoCashOutOdd });
      }
    }
  };

  const functionsForChangBetAmount = {
    [plus]: () => {
      callback(
        decidedCurrentValue(
          amount < maxBet,
          toFixed(plusNumbers(amount, minBet), 2),
          maxBet,
          toFixed(plusNumbers(amount, minBet), 2) > maxBet && showNotification,
          InValidMaxBet
        )
      );
      checkAutoCashOutOddWithBetAmount(
        decidedCurrentValue(amount < maxBet, toFixed(plusNumbers(amount, minBet), 2), maxBet)
      );
    },
    [minus]: () =>
      callback(
        decidedCurrentValue(
          amount > minBet && amount !== minBet && minusNumbers(+amount, Number(minBet)) > minBet,
          toFixed(minusNumbers(amount, Number(minBet)), 2),
          minBet,
          minusNumbers(+amount, Number(minBet)) < minBet && showNotification,
          InValidMinBet
        )
      ),
    [min]: () => {
      if (balance < minBet && isDemo) {
        showNotification({ responseCode: InsufficientFunds });
      }
      doBetStore.setBetAmount(minBet);
    },
    [max]: () => {
      callback(
        decidedCurrentValue(
          balance <= maxBet,
          maxBet,
          maxBet,
          balance <= maxBet && isDemo ? showNotification : undefined,
          balance <= maxBet ? InsufficientFunds : undefined
        )
      );
      checkAutoCashOutOddWithBetAmount(decidedCurrentValue(balance <= maxBet, maxBet, maxBet));
    },
    [divide]: () =>
      callback(
        decidedCurrentValue(
          amount > minBet && divideNumbers(amount, 2) >= minBet,
          toFixed(divideNumbers(amount, 2), 2),
          minBet,
          divideNumbers(amount, 2) < minBet && showNotification,
          InValidMinBet
        )
      ),
    [double]: () => {
      callback(
        decidedCurrentValue(
          amount < maxBet && multiply(amount, 2) < maxBet,
          toFixed(multiply(amount, 2), 2),
          maxBet,
          multiply(amount, 2) > maxBet && showNotification,
          InValidMaxBet
        )
      );
      checkAutoCashOutOddWithBetAmount(
        decidedCurrentValue(
          amount < maxBet && multiply(amount, 2) < maxBet,
          toFixed(multiply(amount, 2), 2),
          maxBet
        )
      );
    },
    [allIn]: () => {
      if (balance > maxBet) {
        showNotification({ responseCode: InValidMaxBet });
      } else if (balance && balance < minBet) {
        showNotification({ responseCode: InValidMinBet });
      } else if (!balance) {
        showNotification({ responseCode: InValidMinBet });
      } else if (balance && balance < maxBet && amount === maxBet) {
        callback(balance < maxBet && amount === maxBet ? toFixed(balance, 2) : toFixed(balance, 2));
      }
      if (amount !== maxBet) {
        callback(
          balance > maxBet ? maxBet : !balance || balance < minBet ? minBet : toFixed(balance, 2)
        );
      }
    },
  };

  functionsForChangBetAmount?.[functionName]();
};

export const changeAutoCashOutOdd = (
  minOdd,
  betAmount,
  maxWin,
  functionName,
  currentOdd,
  value,
  showNotification
) => {
  const { InValidAutoCashOutOdd, InValidMaxAutoCashOutOdd } = BetResponseCode;
  const { plus, minus, typing } = additionalBetAmountChangingButtons;
  const { autoCashOutOdd } = autoCashOutDataKeys;
  const maxAutoCashOutOdd = divideNumbers(maxWin, betAmount);

  const functionsForChangeAutoCashOutOdd = {
    [plus]: () =>
      doBetStore.updateAutoCashOutData(
        autoCashOutOdd,
        decidedCurrentValue(
          plusNumbers(Number(currentOdd), 0.1) < maxAutoCashOutOdd,
          toFixed(plusNumbers(Number(currentOdd), Number(0.1)), 2),
          toFixed(maxAutoCashOutOdd, 2),
          plusNumbers(Number(currentOdd), 0.1) > maxAutoCashOutOdd && showNotification,
          InValidMaxAutoCashOutOdd
        )
      ),
    [minus]: () =>
      doBetStore.updateAutoCashOutData(
        autoCashOutOdd,
        decidedCurrentValue(
          +currentOdd > minOdd && minusNumbers(Number(currentOdd), 0.1) >= minOdd,
          toFixed(minusNumbers(Number(currentOdd), 0.1), 2),
          minOdd,
          minusNumbers(Number(currentOdd), 0.1) < minOdd && showNotification,
          InValidAutoCashOutOdd
        )
      ),
    [typing]: () => doBetStore.updateAutoCashOutData(autoCashOutOdd, value),
  };

  functionsForChangeAutoCashOutOdd?.[functionName]();
};

export const checkDoBetModel = (limits, userBalance, amount) => {
  let generatedBetResponseCode;
  if (Number(amount) < limits.minBet) {
    generatedBetResponseCode = BetResponseCode.InValidMinBet;
  } else if (Number(amount) > limits.maxBet) {
    generatedBetResponseCode = BetResponseCode.InValidMaxBet;
  }
  // if (isDemo && Number(amount) > userBalance) {
  //   generatedBetResponseCode = BetResponseCode.InsufficientFunds;
  // }
  return generatedBetResponseCode;
};

export const betAmountValidator = (betAmount, limits) => {
  let validatedBetAmount = null;
  let errorMessageCode = null;
  const { maxBet, minBet } = limits;

  switch (true) {
    case !maxBet || (betAmount > maxBet && maxBet < minBet): {
      errorMessageCode = BetResponseCode.InsufficientFunds;
      validatedBetAmount = minStake;
      break;
    }
    case Number.isNaN(betAmount) || betAmount <= minBet: {
      if (betAmount < minBet) {
        errorMessageCode = BetResponseCode.InValidMinBet;
      }
      validatedBetAmount = minBet;
      break;
    }
    case betAmount > maxBet: {
      errorMessageCode = BetResponseCode.InValidMaxBet;

      validatedBetAmount = maxBet;
      break;
    }
    default:
      validatedBetAmount = betAmount;
  }

  return { validatedBetAmount, errorMessageCode };
};

export const decideCurrentBetType = (bet, precision = 2) => {
  if (bet?.betData?.markets || bet?.additionalParams?.markets || bet.isReBet) {
    const marketData = marketsStore.markets.find(
      (item) =>
        item.market ===
        (bet?.additionalParams?.markets[0]?.market || bet?.betData?.markets?.[0]?.market)
    );

    const { from, to } = marketData || {};

    return `${from} - ${to}`;
  }
  if (bet.status === BetStatus.Pending) {
    return '- -';
  }
  return `x${parseNumberByPrecision(Number(bet.odd || 0), 1, precision)}`;
};

export const changeMarketsBetsData = (item, odd) => {
  const { amount, odd: winOdd, from, to } = item?.betData?.markets?.[0];
  const { Win, Lose } = BetStatus;
  const isWon = odd >= from && odd <= to;
  const currentOdd = isWon ? winOdd : 0;

  const currentWonAmount = isWon ? parseNumberByPrecision(amount, currentOdd, 2) : 0;
  const currentBetStatus = isWon ? Win : Lose;

  const currentBetAdditionalParams = {
    odd: currentOdd,
    winAmount: currentWonAmount,
    additionalParams: {
      autoCashOutOdd: 0,
      cashOutDuration: null,
      markets: item?.betData?.markets,
    },
    status: currentBetStatus,
  };

  return {
    ...item,
    ...currentBetAdditionalParams,
  };
};

export const filteredDemoBetsWinData = (data) => {
  return data.filter(
    (betData) =>
      !betData?.playerExternalId &&
      !(betData.status === BetStatus.Win) &&
      !betData?.betData?.markets
  );
};
